import { useParams } from "react-router-dom";

import { useT } from "@repo/transifex";
import { useTills } from "~/api/tills/hooks/use-tills";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";
import { ReportStatus } from "~/components";
import { useSalesReportCombined } from "~/hooks/queries/reportsCombined";

import {
  CashDrawerSection,
  DepositFeeSummarySection,
  DiscountsSection,
  PaymentSummarySection,
  RefundsSection,
  SalesByCategoriesSection,
  SalesSummarySection,
  SalesPerUsersSection,
  TransactionSummarySection,
  VatSummarySection
} from "./sections";
import { hasDepositFees, hasDiscounts } from "./sections/utils";
import { useReportCombinedSections } from "./use-report-combined-sections";

export const ReportCombinedList = () => {
  const { sessionId: startDateSeconds } = useParams();

  const { data: reportCombined, status: reportCombinedStatus } =
    useSalesReportCombined(Number(startDateSeconds ?? 0));

  if (reportCombinedStatus !== "success") {
    return <ReportStatus status={reportCombinedStatus} />;
  }

  const receiptCombined = reportCombined.receipt;

  if (!receiptCombined) {
    return <ReportStatus status="empty" />;
  }

  return (
    <ReportCombinedContent
      reports={reportCombined.reports}
      receipt={receiptCombined}
    />
  );
};

type ReportCombinedContentProps = {
  reports: NonNullable<
    ReturnType<typeof useSalesReportCombined>["data"]
  >["reports"];
  receipt: NonNullable<
    NonNullable<ReturnType<typeof useSalesReportCombined>["data"]>["receipt"]
  >;
};

const ReportCombinedContent = ({
  reports,
  receipt
}: ReportCombinedContentProps) => {
  const t = useT();
  // The combined report spans tills of the same location, which all share the
  // partner currency, so the current till's currency is a valid fallback.
  const { currency, isDefault } = useCurrentTillLocalisation();
  const { combined, perTill } = useReportCombinedSections(
    reports,
    receipt,
    isDefault ? undefined : currency
  );
  const { data: tills } = useTills();
  const involvedTillIds = new Set(reports.map((r) => r.generated.tillId));
  const anyTillTakesCash = (tills ?? []).some(
    (till) =>
      involvedTillIds.has(till.id) && till.paymentProviders.includes("cash")
  );

  return (
    <div className="flex flex-col gap-6 pb-0">
      <SalesSummarySection
        title={t("Combined Sales Summary")}
        data={combined.salesSummary}
      />

      {perTill.map((till) => (
        <SalesSummarySection
          key={till.tillName}
          title={t("{tillName} Sales Summary", {
            tillName: till.tillName
          })}
          data={till.salesSummary}
          productsSold={till.productsSold}
        />
      ))}

      {hasDiscounts(combined.discounts) && (
        <DiscountsSection data={combined.discounts} />
      )}
      <RefundsSection data={combined.refunds} />
      <VatSummarySection data={combined.vatSummary} />
      {hasDepositFees(combined.depositFeeSummary) && (
        <DepositFeeSummarySection data={combined.depositFeeSummary} />
      )}
      <PaymentSummarySection data={combined.paymentSummary} />
      {anyTillTakesCash ? (
        <CashDrawerSection data={combined.cashDrawer} />
      ) : null}
      <TransactionSummarySection data={combined.transactionSummary} />
      <SalesByCategoriesSection data={combined.salesByCategories} />
      <SalesPerUsersSection data={combined.salesPerUsers} />
    </div>
  );
};
